
import { mapActions } from 'vuex'
import * as orderTypeEnum from '~/enums/order-type'
import { types as productTypes } from '~/utils/product-item-type'
import * as endpoints from '~/api/endpoints'
import { buyOrderOptions } from '~/utils/periodOptions'
import { createAsyncState, defineAsyncAction } from '~/utils/async-action'
import AddOrderForm from '~/components/create-order/add-order-form.vue'
import Confirmation from '~/components/create-order/order-confirmation.vue'

const DEFAULT_FORMDATA = {
  quantity: 1,
  durationInDays: 365,
  unitPrice: 0,
}

export default {
  components: {
    AddOrderForm,
    Confirmation,
  },
  props: {
    itemId: {
      type: Number,
      required: true,
    },
    itemType: {
      type: String,
      required: true,
      validator: (val) => Object.values(productTypes).includes(val),
    },
    lowestSellPrice: {
      type: Number,
      default: null,
    },
    name: {
      type: String,
      required: true,
    },
    itemIsAnchored: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      periodOptions: buyOrderOptions,
      bidSaveState: createAsyncState(),
      formData: { ...DEFAULT_FORMDATA },
      showConfirmation: false,
      orderTypeEnum,
      transactionFee: 0,
    }
  },
  computed: {
    // The offer price can not be equal or higher than the lowest price
    maxPrice() {
      return this.lowestSellPrice ? this.lowestSellPrice - 0.01 : null
    },

    // Even if there are no bottles on sale, a bid can be made
    hasValidPrice() {
      return this.maxPrice ? this.formData.unitPrice <= this.maxPrice : true
    },

    confirmationText() {
      const { quantity, unitPrice } = this.formData

      const productTypeText = this.$isBottle({ itemType: this.itemType })
        ? this.$ngettext('bottle', 'bottles', quantity)
        : this.$ngettext('bottle from cask', 'bottles from cask', quantity)

      return this.$gettext(
        `Are you sure you want to send an offer for <strong>%(count)d</strong> %(productTypeText)s of %(name)s for <strong>%(price)s</strong>? Expected transaction fee: <strong>%(transactionFee)s</strong>.`,
        {
          count: this.$formatAsNumber(quantity),
          name: this.name,
          price: this.$formatAsCurrency(quantity * unitPrice),
          transactionFee: this.$formatAsCurrency(this.transactionFee),
          productTypeText,
        },
      )
    },
  },
  methods: {
    ...mapActions('trader', ['loadKeyFigures']),

    updateTransactionFee(newTransactionFee) {
      this.transactionFee = newTransactionFee
    },

    openConfirmation() {
      this.showConfirmation = true
    },

    closeConfirmation() {
      this.showConfirmation = false

      this.resetBidSaveState()
    },

    closeForm() {
      this.resetFormData()

      this.$emit('close-form')
    },

    submitForm(formData) {
      this.formData = formData

      if (!this.hasValidPrice) {
        return
      }

      this.openConfirmation()
    },

    resetBidSaveState() {
      this.bidSaveState = createAsyncState()
    },

    placeBid: defineAsyncAction('bidSaveState', async function () {
      const requestData = {
        itemId: this.itemId,
        ...this.formData,
      }

      await this.$axios.$post(endpoints.postExchangeOrderbookBuy, requestData)

      // Update trader key figures
      this.loadKeyFigures()

      this.$triggerToast({
        message: this.$gettext('Offer has been sent'),
      })

      await this.$router.push({
        path: this.$nuxt.context.app.localePath('order-confirmation'),
        query: {
          type: 'buy-order',
          amount: `${this.formData.unitPrice * this.formData.quantity}`,
        },
      })

      this.$dialogs.close()
    }),

    resetFormData() {
      this.formData = { ...DEFAULT_FORMDATA }
    },
  },
}
